import React, { useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PLATFORMS, PlatformKey } from '../config/platformConfig';

interface PlatformTabsProps {
  selectedPlatform: PlatformKey;
  onPlatformChange: (platform: PlatformKey) => void;
  characterCounts: Record<PlatformKey, { current: number; limit: number }>;
}

export default function PlatformTabs({
  selectedPlatform,
  onPlatformChange,
  characterCounts
}: PlatformTabsProps) {
  const platforms = Object.keys(PLATFORMS) as PlatformKey[];
  const selectedIndex = platforms.indexOf(selectedPlatform);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePrevious = () => {
    const newIndex = selectedIndex > 0 ? selectedIndex - 1 : platforms.length - 1;
    onPlatformChange(platforms[newIndex]);
  };

  const handleNext = () => {
    const newIndex = selectedIndex < platforms.length - 1 ? selectedIndex + 1 : 0;
    onPlatformChange(platforms[newIndex]);
  };

  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [selectedIndex]);

  const getCounterColor = (platform: PlatformKey) => {
    const count = characterCounts[platform];
    if (!count) return 'bg-gray-100 text-gray-600';

    const percentage = (count.current / count.limit) * 100;
    if (percentage > 100) return 'bg-red-100 text-red-700 animate-pulse';
    if (percentage > 90) return 'bg-orange-100 text-orange-700';
    if (percentage > 70) return 'bg-yellow-100 text-yellow-700';
    return 'bg-green-100 text-green-700';
  };

  return (
    <div className="bg-white border-b">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left Arrow */}
        <button
          onClick={handlePrevious}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous platform"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        {/* Platform Icons */}
        <div ref={containerRef} className="flex items-center space-x-2 overflow-hidden">
          {platforms.map((key) => {
            const platform = PLATFORMS[key];
            const count = characterCounts[key];
            const isSelected = selectedPlatform === key;

            return (
              <button
                key={key}
                onClick={() => onPlatformChange(key)}
                className={`
                  relative flex items-center justify-center p-3 rounded-lg
                  transition-all duration-200 group
                  ${isSelected
                    ? 'bg-gray-100 shadow-sm scale-110'
                    : 'hover:bg-gray-50 hover:scale-105'
                  }
                `}
                style={{
                  borderWidth: '2px',
                  borderColor: isSelected ? platform.color : 'transparent'
                }}
                aria-label={platform.name}
                title={platform.name}
              >
                <img
                  src={platform.icon}
                  alt={platform.name}
                  className="w-6 h-6"
                  onError={(e) => {
                    e.currentTarget.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${encodeURIComponent(platform.color)}"><circle cx="12" cy="12" r="10"/></svg>`;
                  }}
                />

                {/* Character Count Badge */}
                {count && (
                  <span className={`
                    absolute -top-1 -right-1 text-xs font-bold
                    px-1.5 py-0.5 rounded-full min-w-[20px] text-center
                    ${getCounterColor(key)}
                  `}>
                    {count.current > 999 ? '999+' : count.current}
                  </span>
                )}

                {/* Platform Name Tooltip (on hover) */}
                <div className={`
                  absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2
                  bg-gray-800 text-white text-xs px-2 py-1 rounded
                  whitespace-nowrap opacity-0 group-hover:opacity-100
                  pointer-events-none transition-opacity
                `}>
                  {platform.name}
                  {count && (
                    <span className="ml-1 text-gray-300">
                      ({count.current}/{count.limit})
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Right Arrow */}
        <button
          onClick={handleNext}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next platform"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Active Platform Indicator Bar */}
      <div className="h-1 bg-gray-100">
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${100 / platforms.length}%`,
            marginLeft: `${(selectedIndex * 100) / platforms.length}%`,
            backgroundColor: PLATFORMS[selectedPlatform].color
          }}
        />
      </div>
    </div>
  );
}